import {createResource, getResource, updateResource} from "./common"

export type SignatureAlgorithm = "sha1" | "sha256" | "sha512" | undefined

export enum JWTProviders {
	Auth0 = "Auth0",
	Stytch = "Stytch",
	Cognito = "Cognito",
	Okta = "Okta",
	Custom = "Custom",
	CustomWithJWKS = "CustomWithJWKS",
}

export interface SecretsObject {
	clientId: string
	secret: string
}

export interface PlaidSecrets extends SecretsObject {
	clientId: string
	secret: string
}

export interface AstraSecrets extends SecretsObject {
	clientId: string
	secret: string
}

export type OrgSamlSettings = {
	type: "samlSettings"
	id: string
	attributes: {
		samlSettings: {
			orgWorkspace: string
			entryPoint: string
			cert: string
			signatureAlgorithm: SignatureAlgorithm
			enforceSSOLogin?: boolean
		}
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: number
			}
		}
	}
}

export type OrgSettings = {
	type: "orgSettings"
	id: string
	attributes: {
		generalSettings: {
			preventDuplicateIndividualApplications: boolean
			showSensitiveDataForPhysicalCards: boolean
			enableTagInheritanceForCards: boolean
			programmaticAuthorizationHttpPostURL: string | null
			secretToken: string | null
			enableReevaluateApplication: boolean
		}
		samlSettings?: {
			orgWorkspace: string
			entryPoint: string
			cert: string
			signatureAlgorithm: SignatureAlgorithm
			enforceSSOLogin?: boolean
		}
		jwtSettings?: {
			provider: JWTProviders
			jwksPath: string
			issuer?: string
		}
		dashboardSettings?: {
			whitelistSourceIps: string
		}
		customerTagInheritancePolicy?: {
			keysToInheritForCards: string[]
			keysToInheritForAccounts: string[]
		}
		statementsSettings: {
			statementFont?: string
			statementLogo?: string
			statementHeaderBackground?: string
			statementHeaderColor?: string
			statementPhone: string
			statementEmail: string
			statementRepresentativeName?: string
			whiteLabelDefaultThemeId?: string
			statementCreditWebsite: string
			statementCreditCustomerSupportInfo: string
			statementCreditCardHolderAgreement: string
			statementCreditDocumentation: string
			statementCreditLatePaymentWarning: string
			statementCreditInterestChargeCalculationDescription: string
		}
		applicationFormSettings: {
			applicationFormCustomCss?: string
			applicationFormLogo?: string
			applicationFormFavicon?: string
			applicationFormBrandName?: string
			applicationFormRedirectUrl?: string
			applicationFormRedirectButtonText?: string
			applicationFormDeniedRedirectUrl?: string
			applicationFormPrivacyPolicyUrl?: string
			applicationFormElectronicDisclosuresUrl?: string
			applicationFormDepositTermsUrl?: string
			applicationFormClientTermsUrl?: string
			applicationFormCardholderTermsUrl?: string
			applicationFormCashAdvancedTermsUrl?: string
			applicationFormDebitCardDisclosureUrl?: string
			applicationFormContactUsEmail?: string
			applicationFormBillPayTermsOfUseUrl?: string
			applicationFormBillPayServiceAgreementUrl?: string
			applicationFormBillPayElectronicDisclosureUrl?: string
			applicationFormAdditionalDisclosures?: Array<ApplicationFormAdditionalDisclosure>
			applicationFormValidatePhoneNumber?: boolean
			applicationFormUseSelfieVerification?: boolean
			applicationFormShowElectronicDisclosureStage?: boolean
			applicationFormShowTaxStage?: boolean
			applicationFormEnableOutreach?: boolean
			bankingPageURL?: string
			applicationFormOutreachSettingsId?: string
			applicationFormBusinessElectronicDisclosureUrl?: string
			applicationFormBusinessDepositAccountAgreementUrl?: string
			applicationFormBusinessDepositAccountDisclosuresUrl?: string
			applicationFormBusinessDebitCardholderAgreementUrl?: string
			applicationFormBusinessTermsOfUseUrl?: string
			applicationFormBusinessPrivacyPolicyUrl?: string
			applicationFormPaymentProcessorTermsUrl?: string
		}
		applicationFormDomainSettings: {
			applicationFormDomain?: string
			applicationFormCertificateStatus?: "Verified" | "Unverified"
		}
		capitalSettings?: {
			capitalPageUrl?: string
		} | null
		billpaySettings?: {
			billpayPageUrl?: string
			billpayReactivationPageUrl?: string
		} | null
		createdAt: Date
		updatedAt?: Date
		plaidSecretsSet?: boolean
		astraSecretsSet?: boolean
		fallbackBankName?: string
		paymentProcessorSettings?: {
			rPaymentsProcessorSettings?: PaymentProcessorSettings
		}
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: number
			}
		}
	}
}

export type PaymentProcessorSettings = {
	webhookSecretSet: boolean
	apiKeySet: boolean
	yourAccountWebhookSecretSet?: boolean
	publishableKey?: string
	hostname?: string
	filesHostname?: string
	mccCodes?: string[]
	connectedAccountLookupUrl?: string
}

export type OrgPaymentProcessorSettings = {
	type: "paymentProcessorSettings"
	id: string
	attributes: {
		paymentProcessorSettings: {
			rPaymentsProcessorSettings?: PaymentProcessorSettings
		}
	}
	relationships: {
		org: {
			data: {
				type: "org"
				id: number
			}
		}
	}
}

export type StatementLogo = {
	type: "orgSettingsStatementLogo"
	attributes: {
		statementLogo?: string
	}
}

type OrgSettingsPatch = {
	generalSettings?: {
		preventDuplicateIndividualApplications: boolean
		showSensitiveDataForPhysicalCards: boolean
		enableTagInheritanceForCards: boolean
		programmaticAuthorizationHttpPostURL: string | null
		secretToken: string | null
		enableReevaluateApplication: boolean
	} | null
	customerTagInheritancePolicy?: {
		keysToInheritForCards: string[]
		keysToInheritForAccounts: string[]
	} | null
	samlSettings?: {
		orgWorkspace: string
		entryPoint: string
		cert: string
		signatureAlgorithm: SignatureAlgorithm
		enforceSSOLogin?: boolean
	} | null
	jwtSettings?: {
		provider: JWTProviders
		jwksPath: string
		issuer?: string
	}
	dashboardSettings?: {
		whitelistSourceIps: string
	} | null
	statementsSettings?: {
		statementFont?: string
		statementLogo?: string
		statementHeaderBackground?: string
		statementHeaderColor?: string
		statementPhone: string
		statementEmail: string
		statementRepresentativeName?: string
		whiteLabelDefaultThemeId?: string
		statementCreditWebsite?: string
		statementCreditCustomerSupportInfo?: string
		statementCreditDocumentation?: string
		statementCreditLatePaymentWarning?: string
		statementCreditInterestChargeCalculationDescription?: string
	} | null
	capitalSettings?: {
		capitalPageUrl?: string
	} | null
	billpaySettings?: {
		billpayPageUrl?: string
		billpayReactivationPageUrl?: string
	} | null
	applicationFormSettings?: {
		applicationFormCustomCss?: string
		applicationFormLogo?: string
		applicationFormFavicon?: string
		applicationFormBrandName?: string
		applicationFormRedirectUrl?: string
		applicationFormDeniedRedirectUrl?: string
		applicationFormPrivacyPolicyUrl?: string
		applicationFormElectronicDisclosuresUrl?: string
		applicationFormDepositTermsUrl?: string
		applicationFormClientTermsUrl?: string
		applicationFormCardholderTermsUrl?: string
		applicationFormCashAdvancedTermsUrl?: string
		applicationFormContactUsEmail?: string
		applicationFormBillPayTermsOfUseUrl?: string
		applicationFormBillPayServiceAgreementUrl?: string
		applicationFormBillPayElectronicDisclosureUrl?: string
		applicationFormValidatePhoneNumber?: boolean
		applicationFormBusinessElectronicDisclosureUrl?: string
		applicationFormBusinessDepositAccountAgreementUrl?: string
		applicationFormBusinessDepositAccountDisclosuresUrl?: string
		applicationFormBusinessDebitCardholderAgreementUrl?: string
		applicationFormBusinessTermsOfUseUrl?: string
		applicationFormBusinessPrivacyPolicyUrl?: string
		applicationFormPaymentProcessorTermsUrl?: string
	}
	fallbackBankName?: string
}

export type DnsSetting = {
	type: "domainDnsSetting"
	attributes: {
		key: string
		value: string
	}
}

export type ApplicationFormAdditionalDisclosure = {title: string; url: string}

export type SamlSettingsPatchPayload = {
	orgWorkspace: string
	entryPoint: string
	cert: string
	signatureAlgorithm: SignatureAlgorithm
	enforceSSOLogin: boolean
}

export async function getByToken(accessToken: string) {
	const result = await getResource<OrgSettings>("org-settings", accessToken)
	return result.map((v) => v.data)
}

export async function getSamlSettingsByToken(accessToken: string) {
	const result = await getResource<OrgSamlSettings>("org-settings/saml-settings", accessToken)
	return result.map((v) => v.data)
}

export async function getByOrg(accessToken: string, id: string) {
	const result = await getResource<OrgSettings>(`orgs/${id}/org-settings`, accessToken)
	return result.map((v) => v.data)
}

export async function getStatementLogoByToken(accessToken: string) {
	const result = await getResource<StatementLogo>("org-settings/statement-logo", accessToken)
	return result.map((v) => v.data)
}

export async function update(accessToken: string, patch: OrgSettingsPatch, org?: string) {
	const result = await updateResource<OrgSettings>("org-settings", accessToken, {
		type: "orgSettings",
		attributes: {
			...patch,
		},
		relationships: org
			? {
					org: {
						data: {
							type: "org",
							id: org,
						},
					},
			  }
			: undefined,
	})
	return result.map((v) => v.data)
}

export async function patchSamlSettings(accessToken: string, samlSettings: SamlSettingsPatchPayload | null) {
	const result = await updateResource<OrgSamlSettings>("org-settings/saml-settings", accessToken, {
		type: "patchSamlSettings",
		attributes: {
			samlSettings,
		},
	})
	return result.map((v) => v.data)
}

export async function setupDomain(accessToken: string, domainName: string) {
	const result = await createResource<OrgSettings>("org-settings/setup-domain", accessToken, {
		domainName,
	})
	return result.map((v) => v.data)
}

export async function VerifyDomain(accessToken: string) {
	const result = await createResource<OrgSettings>("org-settings/link-domain", accessToken, {})
	return result.map((v) => v.data)
}

export async function deleteDomain(accessToken: string) {
	const result = await createResource<OrgSettings>("org-settings/delete-domain", accessToken, {})
	return result.map((v) => v.data)
}

export async function getClientDomainConfig(accessToken: string) {
	const result = await getResource<Array<DnsSetting>>("org-settings/get-client-domain-config", accessToken)
	return result.map((v) => v.data)
}

export async function patchPlaidSecrets(accessToken: string, secretObject: PlaidSecrets) {
	const result = await updateResource("org-settings/plaid-secrets", accessToken, {
		type: "plaidSecrets",
		attributes: {...secretObject},
	})

	return result.map((v) => v.data)
}

export async function patchAstraSecrets(accessToken: string, secretObject: AstraSecrets) {
	const result = await updateResource("org-settings/astra-secrets", accessToken, {
		type: "astraSecrets",
		attributes: {...secretObject},
	})
	return result.map((v) => v.data)
}

export async function updateAppConfigPaymentProcessing(
	accessToken: string,
	attributes: {
		webhookSecret?: string | null
		yourAccountWebhookSecret?: string | null
		apiKey?: string | null
		publishableKey?: string | null
		hostname?: string
		filesHostname?: string
		mccCodes?: string[]
		connectedAccountLookupUrl?: string | null
	},
	type: string
) {
	const data = {
		type,
		attributes,
	}

	const result = await updateResource("org-settings/payment-processor-settings", accessToken, data)
	return result.map((v) => v.data)
}

export async function getPaymentProcessorSettings(accessToken: string) {
	const result = await getResource<OrgPaymentProcessorSettings>("org-settings/payment-processor-settings", accessToken)
	return result.map((v) => v.data?.attributes.paymentProcessorSettings.rPaymentsProcessorSettings)
}
